import moment from 'moment';

/**
 * The scope vocabulary every GSC tool report shares: the windows it may ask for, the surfaces it
 * may read, and the arithmetic that turns one window into the window it is compared against.
 *
 * None of it is Search Console's own limits — the API would happily return 16 months. They are
 * GDDash bounds, kept here rather than in each report so two tools cannot disagree about what a
 * "previous period" is.
 */

/** Product bound on one report period. Two of these are affordable; a year of days is not. */
export const MAX_PERIOD_DAYS = 90;

/**
 * Product bound on a *detail* period — one asked for with a page or query dimension, which
 * multiplies the rows retrieved per day and so is held to a shorter window than the headline.
 */
export const DETAIL_MAX_DAYS = 28;

/** How many days a report defaults to when it has nothing better to go on. */
export const DEFAULT_PERIOD_DAYS = 28;

/**
 * Search Console reports in America/Los_Angeles calendar dates and keeps collecting the most
 * recent ones, so a default window stops short of today rather than ending in days that are
 * still filling up.
 */
export const REPORTING_LAG_DAYS = 2;

/** The timezone every date in every GSC report is a calendar date in. */
export const REPORTING_TIMEZONE = 'America/Los_Angeles';

/**
 * How far back Search Console keeps performance data. A window starting before this floor can
 * only come back empty, so the controls stop the calendar there instead of letting a user pick
 * dates that have nothing behind them.
 */
export const RETENTION_MONTHS = 16;

/**
 * The windows the date picker offers as one click, shortest first. A detail report, held to a
 * shorter bound than the headline one, drops the presets it could not ask for.
 */
export const PERIOD_PRESETS = [7, 14, 28, 90];

export const COMPARISON = {
    PREVIOUS: 'previous',
    YEAR_OVER_YEAR: 'year_over_year',
    CUSTOM: 'custom',
    NONE: 'none',
};

/**
 * Search Console's `type` values, in the order the controls offer them.
 *
 * "web" is the API's own default — the combined ("All") tab of Google Search, which excludes
 * Discover and Google News — so every other value narrows a report to one surface rather than
 * adding to it. The API's deprecated `searchType` is not sent; `type` is.
 */
export const SEARCH_TYPES = ['web', 'discover', 'googleNews', 'news', 'image', 'video'];

export const DEFAULT_SEARCH_TYPE = 'web';

/**
 * Surfaces Search Console reports no ranking position for. Their rows still carry clicks,
 * impressions and CTR, so a report is read the same way minus one metric — which the controls
 * say up front rather than letting an empty average position read as a missing answer.
 */
export const NO_POSITION_TYPES = new Set(['discover', 'googleNews']);

export const DATA_STATE = { FINAL: 'final', ALL: 'all' };

/**
 * The label for a search type, falling back to the raw API value for anything unrecognised.
 *
 * The strings live in the shared `tools.gsc` namespace: every tool names these six surfaces, and
 * they have to be named identically or two reports on the same surface would appear to be on
 * different ones.
 */
export function searchTypeLabel(type, t) {
    return SEARCH_TYPES.includes(type) ? t(`tools.gsc.search_types.${type}`) : type;
}

const FORMAT = 'YYYY-MM-DD';

/** Inclusive day count of a period, or 0 when either end is missing or the period is inverted. */
export function inclusiveDays(period) {
    if (!period?.startDate || !period?.endDate) return 0;
    const days = moment(period.endDate).diff(moment(period.startDate), 'days') + 1;
    return days > 0 ? days : 0;
}

/** Every calendar date from `startDate` to `endDate` inclusive, as YYYY-MM-DD strings. */
export function dateAxis(period) {
    const dates = [];
    if (!period?.startDate || !period?.endDate) return dates;

    const cursor = moment(period.startDate);
    const last = moment(period.endDate);
    while (cursor.isSameOrBefore(last)) {
        dates.push(cursor.format(FORMAT));
        cursor.add(1, 'days');
    }
    return dates;
}

/** A report's starting window: `days` of dates ending clear of the reporting lag. */
export function defaultPeriod(days = DEFAULT_PERIOD_DAYS) {
    const endDate = moment().startOf('day').subtract(REPORTING_LAG_DAYS, 'days');
    return {
        startDate: endDate
            .clone()
            .subtract(days - 1, 'days')
            .format(FORMAT),
        endDate: endDate.format(FORMAT),
    };
}

/** `days` of dates ending on `endDate`, used to seed the controls from a probe. */
export function periodEndingOn(endDate, days = DEFAULT_PERIOD_DAYS) {
    const last = moment(endDate);
    return {
        startDate: last
            .clone()
            .subtract(days - 1, 'days')
            .format(FORMAT),
        endDate: last.format(FORMAT),
    };
}

/**
 * The window immediately before `period`, of the same length and touching it, so the two together
 * form one contiguous stretch of days with no overlap and no gap.
 */
function previousPeriod(period) {
    const days = inclusiveDays(period);
    if (!days) return null;

    const endDate = moment(period.startDate).subtract(1, 'days');
    return {
        startDate: endDate
            .clone()
            .subtract(days - 1, 'days')
            .format(FORMAT),
        endDate: endDate.format(FORMAT),
    };
}

/**
 * The same window one year earlier.
 *
 * Moment clamps 29 February to the 28th when the target year is not a leap year, which is the
 * behaviour wanted here — the alternative, rolling into 1 March, would silently shift the window.
 * The clamp can leave the baseline a day shorter or longer than the current period, so the two
 * day counts are compared by the caller and surfaced rather than hidden.
 */
function yearOverYearPeriod(period) {
    if (!period?.startDate || !period?.endDate) return null;
    return {
        startDate: moment(period.startDate).subtract(1, 'years').format(FORMAT),
        endDate: moment(period.endDate).subtract(1, 'years').format(FORMAT),
    };
}

/**
 * Resolves the baseline period for a comparison mode. Returns null for "no comparison", which
 * suppresses every prior figure on a report and disables whatever depends on a baseline.
 */
export function resolveBaseline(period, comparison, customBaseline) {
    switch (comparison) {
        case COMPARISON.PREVIOUS:
            return previousPeriod(period);
        case COMPARISON.YEAR_OVER_YEAR:
            return yearOverYearPeriod(period);
        case COMPARISON.CUSTOM:
            return customBaseline?.startDate && customBaseline?.endDate
                ? { ...customBaseline }
                : null;
        default:
            return null;
    }
}

/**
 * The detail window for a period: the same dates when they fit inside `maxDays`, otherwise its
 * last `maxDays` days. Shortening is reported to the caller so a block can say it covers less
 * than the headline rather than appearing to disagree with it.
 */
export function detailPeriod(period, maxDays = DETAIL_MAX_DAYS) {
    const days = inclusiveDays(period);
    if (!days || days <= maxDays) return { period, shortened: false };

    return { period: periodEndingOn(period.endDate, maxDays), shortened: true };
}

/**
 * Day-index alignment between two windows: for each ordinal day of `current`, the date that sits
 * at the same offset in `baseline`.
 *
 * Previous-period and year-over-year comparisons are both read this way — day 1 against day 1 —
 * because a window laid over the one before it has no shared dates to align on. Year over year
 * gets its own date mapping out of this rather than assuming 365 days, which would drift through
 * a leap year.
 */
export function alignDays(current, baseline) {
    const currentDates = dateAxis(current);
    const baselineDates = dateAxis(baseline);
    const length = Math.max(currentDates.length, baselineDates.length);

    return Array.from({ length }, (_, index) => ({
        day: index + 1,
        currentDate: currentDates[index] ?? null,
        baselineDate: baselineDates[index] ?? null,
    }));
}

/** The earliest date Search Console can still answer for. */
export function retentionFloor() {
    return moment().subtract(RETENTION_MONTHS, 'months').format(FORMAT);
}

/**
 * The latest date a calendar may offer. It is today rather than the last day with final data:
 * the most recent days are exactly what "include preliminary data" is for, so the controls let
 * them be picked instead of hiding them behind a bound the checkbox could not undo.
 */
export function reportingCeiling() {
    return moment().format(FORMAT);
}

/**
 * The preset windows for the date picker, each resolving to `days` of dates ending on `anchor` —
 * the property's most recent day with data when one is known, so a preset lands on days that have
 * something in them rather than on the days Search Console has not filled yet.
 */
export function presetOptions(t, { maxDays = MAX_PERIOD_DAYS } = {}) {
    return PERIOD_PRESETS.filter((days) => days <= maxDays).map((days) => ({
        id: `${days}_days`,
        key: 'dates',
        days,
        label: t('tools.gsc.days', { days }),
    }));
}

/** The calendar day immediately before `date`. */
export function dayBefore(date) {
    return moment(date).subtract(1, 'days').format(FORMAT);
}

/** The `{ start_date, end_date }` pair a preset resolves to, in the picker's own shape. */
export function presetRange(preset, anchor) {
    const period = periodEndingOn(anchor, preset?.days ?? DEFAULT_PERIOD_DAYS);
    return { start_date: period.startDate, end_date: period.endDate };
}

/** Human-readable "01 Sept 2026 – 28 Sept 2026" for a period, or an em dash when there is none. */
export function formatPeriod(period) {
    if (!period?.startDate || !period?.endDate) return '—';
    return `${moment(period.startDate).format('DD MMM YYYY')} – ${moment(period.endDate).format(
        'DD MMM YYYY',
    )}`;
}
